import { useId, type CSSProperties } from 'react';
import { ICONS, isIconId, shapeOutline, type Fill, type ShapeLayer } from '@bannermatic/core';
import { boxShadow, fillStyle } from '../style';

const DASH = { solid: undefined, dashed: '6 4', dotted: '1 3' } as const;

function dropShadow(layer: ShapeLayer): string | undefined {
  return layer.shadow
    ? `drop-shadow(${layer.shadow.x}px ${layer.shadow.y}px ${layer.shadow.blur / 2}px ${layer.shadow.color})`
    : undefined;
}

/** SVG paint for icons: solid colours as is, gradients as SVG gradients over the icon box. */
function SvgPaint({ id, fill }: { id: string; fill: Fill }) {
  if (fill.type === 'solid') return null;
  const stops = [...fill.stops]
    .sort((a, b) => a.offset - b.offset)
    .map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />);
  if (fill.type === 'radial')
    return (
      <radialGradient id={id} cx="0.5" cy="0.5" r="0.71">
        {stops}
      </radialGradient>
    );
  // Same direction convention as CSS: 0deg points up, angles go clockwise
  const a = (fill.angle * Math.PI) / 180;
  const dx = Math.sin(a) / 2;
  const dy = -Math.cos(a) / 2;
  return (
    <linearGradient id={id} x1={0.5 - dx} y1={0.5 - dy} x2={0.5 + dx} y2={0.5 + dy}>
      {stops}
    </linearGradient>
  );
}

function IconView({ layer }: { layer: ShapeLayer }) {
  const gid = `bm-icon-${useId().replace(/:/g, '')}`;
  if (!isIconId(layer.icon) || !layer.fill) return null;
  const paint = layer.fill.type === 'solid' ? layer.fill.color : `url(#${gid})`;
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      fill="none"
      stroke={paint}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block', overflow: 'visible', filter: dropShadow(layer) }}
    >
      <defs>
        <SvgPaint id={gid} fill={layer.fill} />
      </defs>
      {/* Markup comes from the built-in icon set only (looked up by id), never from user input */}
      <g dangerouslySetInnerHTML={{ __html: ICONS[layer.icon].svg }} />
    </svg>
  );
}

/**
 * Stars, hearts, arrows, bubbles…: a CSS box (so fills and gradients behave exactly as on
 * rectangles) clipped by the outline in the unit box, with the stroke drawn on top.
 */
function OutlineView({ layer, d }: { layer: ShapeLayer; d: string }) {
  const clipId = `bm-clip-${useId().replace(/:/g, '')}`;
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', filter: dropShadow(layer) }}>
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
        <clipPath id={clipId} clipPathUnits="objectBoundingBox">
          <path d={d} clipRule="evenodd" />
        </clipPath>
      </svg>
      {layer.fill && (
        <div
          style={{
            width: '100%',
            height: '100%',
            ...fillStyle(layer.fill),
            clipPath: `url(#${clipId})`,
          }}
        />
      )}
      {layer.strokeWidth > 0 && (
        <svg
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            overflow: 'visible',
          }}
        >
          <path
            d={d}
            fill="none"
            fillRule="evenodd"
            stroke={layer.strokeColor}
            strokeWidth={layer.strokeWidth}
            strokeLinejoin="round"
            strokeDasharray={
              DASH[layer.strokeStyle] &&
              DASH[layer.strokeStyle]!.split(' ')
                .map((n) => Number(n) * Math.max(1, layer.strokeWidth))
                .join(' ')
            }
            strokeLinecap={layer.strokeStyle === 'dotted' ? 'round' : undefined}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}
    </div>
  );
}

export function ShapeLayerView({ layer }: { layer: ShapeLayer }) {
  const stroke =
    layer.strokeWidth > 0
      ? `${layer.strokeWidth}px ${layer.strokeStyle} ${layer.strokeColor}`
      : undefined;

  if (layer.shape === 'line') {
    const thickness = Math.max(1, layer.strokeWidth || 2);
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            marginTop: -thickness / 2,
            borderTop: `${thickness}px ${layer.strokeStyle} ${layer.strokeColor}`,
            boxShadow: boxShadow(layer.shadow),
          }}
        />
      </div>
    );
  }

  if (layer.shape === 'icon') return <IconView layer={layer} />;

  const outline = shapeOutline(layer);
  if (outline) return <OutlineView layer={layer} d={outline} />;

  const style: CSSProperties = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    ...fillStyle(layer.fill),
  };
  return (
    <div
      style={{
        ...style,
        border: stroke,
        borderRadius: layer.shape === 'ellipse' ? '50%' : layer.borderRadius || undefined,
        boxShadow: boxShadow(layer.shadow),
      }}
    />
  );
}
